import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import ChannelIcon from '../campaigns/ChannelIcon';

/**
 * 캠페인 지원자 관리 — every application across every campaign in one flat,
 * filterable table, styled to match the /admin/users list.
 *
 * The previous version grouped rows by campaign behind expandable accordions,
 * which hid the thing operators actually work from: the queue of applications
 * waiting on a decision. Finding one applicant meant expanding campaigns one by
 * one, and campaign-level counts said nothing about who was blocking.
 *
 * Statuses come straight from the `MatchStatus` enum — nothing is synthesised.
 * Each status is relabelled as the task it represents so the stats strip reads
 * as a work queue rather than a status snapshot.
 */

interface TaskStep {
  status: string;
  taskKey: string;
  defaultTask: string;
  /** Who is blocking. `null` when the status is terminal. */
  ownerKey: string | null;
  defaultOwner: string | null;
  badgeClass: string;
}

/**
 * Pipeline order mirrors the real progression in the matches lambda:
 * applied → shortlisted → requested → accepted → completed.
 */
const TASK_PIPELINE: TaskStep[] = [
  {
    status: 'applied',
    taskKey: 'matches.task.applied',
    defaultTask: '1차 검토 대기',
    ownerKey: 'matches.owner.ops',
    defaultOwner: '운영팀',
    badgeClass: 'bg-gray-100 text-gray-700',
  },
  {
    status: 'shortlisted',
    taskKey: 'matches.task.shortlisted',
    defaultTask: '선정 여부 결정 대기',
    ownerKey: 'matches.owner.ops',
    defaultOwner: '운영팀',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
  {
    status: 'requested',
    taskKey: 'matches.task.requested',
    defaultTask: '인플루언서 수락 대기',
    ownerKey: 'matches.owner.influencer',
    defaultOwner: '인플루언서',
    badgeClass: 'bg-blue-100 text-blue-700',
  },
  {
    status: 'accepted',
    taskKey: 'matches.task.accepted',
    defaultTask: '체험 일정 관리',
    ownerKey: 'matches.owner.ops',
    defaultOwner: '운영팀',
    badgeClass: 'bg-green-100 text-green-700',
  },
];

/** Terminal statuses — nothing outstanding, so no owner. */
const TERMINAL_STEPS: TaskStep[] = [
  {
    status: 'completed',
    taskKey: 'matches.task.completed',
    defaultTask: '완료',
    ownerKey: null,
    defaultOwner: null,
    badgeClass: 'bg-indigo-100 text-indigo-700',
  },
  {
    status: 'not_selected',
    taskKey: 'matches.task.not_selected',
    defaultTask: '미선정',
    ownerKey: null,
    defaultOwner: null,
    badgeClass: 'bg-gray-100 text-gray-500',
  },
  {
    status: 'rejected',
    taskKey: 'matches.task.rejected',
    defaultTask: '거절',
    ownerKey: null,
    defaultOwner: null,
    badgeClass: 'bg-red-100 text-red-700',
  },
  {
    status: 'canceled',
    taskKey: 'matches.task.canceled',
    defaultTask: '취소',
    ownerKey: null,
    defaultOwner: null,
    badgeClass: 'bg-gray-100 text-gray-500',
  },
  {
    status: 'expired',
    taskKey: 'matches.task.expired',
    defaultTask: '만료',
    ownerKey: null,
    defaultOwner: null,
    badgeClass: 'bg-gray-100 text-gray-500',
  },
];

const ALL_STEPS = [...TASK_PIPELINE, ...TERMINAL_STEPS];
const STEP_BY_STATUS: Record<string, TaskStep> = Object.fromEntries(
  ALL_STEPS.map((s) => [s.status, s]),
);
/** Pipeline position, used when sorting by task. */
const STATUS_ORDER: Record<string, number> = Object.fromEntries(
  ALL_STEPS.map((s, i) => [s.status, i]),
);

const PAGE_SIZE = 50;

type SortKey = 'influencer' | 'campaign' | 'followers' | 'status' | 'appliedAt' | 'updatedAt';
type SortDir = 'asc' | 'desc';

interface ApplicationRow {
  matchId: string;
  campaignId: string;
  influencerId: string;
  status: string;
  appliedAt: string;
  updatedAt: string;
  influencerName: string;
  handle: string;
  profileImage?: string;
  channels: string[];
  categories: string[];
  followers: number;
  campaignName: string;
  campaignRegion: string;
}

/** Channels the profile form collects; `naverblog` renders as `blog`. */
const SNS_KEYS: ReadonlyArray<{ key: string; platform: string }> = [
  { key: 'instagram', platform: 'instagram' },
  { key: 'youtube', platform: 'youtube' },
  { key: 'naverblog', platform: 'blog' },
];

/** Total reach across the channels the influencer filled in. */
const sumFollowers = (handles: any): number => {
  if (!handles) return 0;
  return SNS_KEYS.reduce((total, { key }) => {
    const n = handles[key]?.followerCount;
    return total + (typeof n === 'number' && n > 0 ? n : 0);
  }, 0);
};

const presentChannels = (handles: any): string[] => {
  if (!handles) return [];
  return SNS_KEYS.filter(({ key }) => {
    const ch = handles[key];
    return ch && (ch.account || ch.followerCount);
  }).map(({ platform }) => platform);
};

/**
 * Best-effort display handle. Accounts are stored as full profile URLs, so
 * strip the known prefixes back down to the bare handle.
 */
const deriveHandle = (handles: any): string => {
  if (!handles) return '';
  for (const { key } of SNS_KEYS) {
    const raw = handles[key]?.account;
    if (!raw) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const bare = trimmed
      .replace(/^https?:\/\/(www\.)?/i, '')
      .replace(/^instagram\.com\//i, '')
      .replace(/^youtube\.com\/(@)?/i, '')
      .replace(/^blog\.naver\.com\//i, '')
      .replace(/\/+$/, '');
    if (bare) return bare.startsWith('@') ? bare : `@${bare}`;
  }
  return '';
};

/**
 * The influencer's interest categories.
 *
 * Read `categories[]`, not `mainCategory`. The profile UI only ever writes the
 * array — nothing sets `mainCategory`, so it keeps whatever the creation path
 * seeded ('other' from post-confirmation, '' from the auth lambda). Rendering
 * it made every row read "기타". `mainCategory` is kept only as a fallback for
 * rows that predate the array.
 */
const resolveCategories = (inf: any): string[] => {
  const list = Array.isArray(inf?.categories) ? inf.categories.filter(Boolean) : [];
  if (list.length > 0) return list;
  const main = inf?.mainCategory;
  return main && main !== 'other' ? [main] : [];
};

const fmtFollowers = (n: number): string => {
  if (n <= 0) return '-';
  if (n >= 10000) return `${(n / 10000).toFixed(1).replace(/\.0$/, '')}만`;
  return n.toLocaleString('ko-KR');
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Absolute local date-time. Company policy: calendar days, no business days. */
const fmtDateTime = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '-';
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CampaignMatchesPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();

  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [influencerQuery, setInfluencerQuery] = useState('');
  const [campaignQuery, setCampaignQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | null>('appliedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // getAllCampaigns pages the cursor; a single getCampaigns call returns
        // one capped Scan page and would leave campaigns unresolved.
        const [matchRes, campRes] = await Promise.all([
          graphqlApi.getMatches({}),
          graphqlApi.getAllCampaigns({}),
        ]);

        const rawMatches: any[] =
          matchRes.success && matchRes.data ? matchRes.data.matches || [] : [];
        const campaigns: any[] = campRes.success && campRes.data ? campRes.data : [];

        const campaignMap: Record<string, any> = {};
        for (const c of campaigns) campaignMap[c.campaignId] = c;

        const influencerIds = Array.from(
          new Set(rawMatches.map((m) => m.influencerId).filter(Boolean)),
        );
        const influencerMap: Record<string, any> = {};
        if (influencerIds.length > 0) {
          try {
            const infRes = await graphqlApi.getInfluencersByIds(influencerIds);
            if (infRes.success && infRes.data) {
              for (const inf of infRes.data) influencerMap[inf.influencerId] = inf;
            }
          } catch {
            /* enrichment is best-effort — fall back to the id */
          }
        }

        const built: ApplicationRow[] = rawMatches
          .filter((m) => m.campaignId)
          .map((m) => {
            const camp = campaignMap[m.campaignId];
            const inf = influencerMap[m.influencerId];
            const handles = inf?.socialMediaHandles;
            const region = [camp?.visitCity, camp?.visitCountry].filter(Boolean).join('·');
            return {
              matchId: m.matchId,
              campaignId: m.campaignId,
              influencerId: m.influencerId,
              status: m.status,
              appliedAt: m.appliedAt || m.createdAt || '',
              updatedAt: m.updatedAt || m.appliedAt || m.createdAt || '',
              influencerName: inf?.name || inf?.nickname || m.influencerId,
              handle: deriveHandle(handles),
              profileImage: inf?.profileImage,
              channels: presentChannels(handles),
              categories: resolveCategories(inf),
              followers: sumFollowers(handles),
              campaignName: camp?.campaignName || t('matches.unknownCampaign', '(삭제된 캠페인)'),
              campaignRegion: region || camp?.brandName || '',
            };
          });

        if (!cancelled) setRows(built);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load applications');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  /** Counts over the full dataset so the strip doesn't shift while filtering. */
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter) out = out.filter((r) => r.status === statusFilter);

    const iq = influencerQuery.trim().toLowerCase();
    if (iq) {
      out = out.filter(
        (r) => r.influencerName.toLowerCase().includes(iq) || r.handle.toLowerCase().includes(iq),
      );
    }
    const cq = campaignQuery.trim().toLowerCase();
    if (cq) {
      out = out.filter(
        (r) =>
          r.campaignName.toLowerCase().includes(cq) || r.campaignRegion.toLowerCase().includes(cq),
      );
    }

    if (!sortKey) return out;

    const pick = (r: ApplicationRow): string | number => {
      switch (sortKey) {
        case 'influencer': return r.influencerName;
        case 'campaign': return r.campaignName;
        case 'followers': return r.followers;
        case 'status': return STATUS_ORDER[r.status] ?? 99;
        case 'updatedAt': return new Date(r.updatedAt).getTime() || 0;
        default: return new Date(r.appliedAt).getTime() || 0;
      }
    };

    return [...out].sort((a, b) => {
      const av = pick(a);
      const bv = pick(b);
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'ko') : bv.localeCompare(av, 'ko');
      }
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [rows, statusFilter, influencerQuery, campaignQuery, sortKey, sortDir]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const pageNumbers = useMemo(() => {
    const window = 3;
    const from = Math.max(1, safePage - window);
    const to = Math.min(pageCount, safePage + window);
    const arr: number[] = [];
    for (let p = from; p <= to; p++) arr.push(p);
    return arr;
  }, [safePage, pageCount]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'influencer' || key === 'campaign' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  /** Clicking a stats cell toggles that status filter. */
  const toggleStatus = (status: string) => {
    setStatusFilter((prev) => (prev === status ? '' : status));
    setPage(1);
  };

  const taskLabel = (step: TaskStep) => t(step.taskKey, step.defaultTask);

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) {
      return <ChevronUpDownIcon className="w-3.5 h-3.5 text-gray-300 inline-block" />;
    }
    return sortDir === 'asc' ? (
      <ChevronUpIcon className="w-3.5 h-3.5 text-blue-600 inline-block" />
    ) : (
      <ChevronDownIcon className="w-3.5 h-3.5 text-blue-600 inline-block" />
    );
  };

  const headerCell = (columnKey: SortKey, labelKey: string, defaultLabel: string) => (
    <th className="px-4 py-3 text-left font-medium">
      <button
        type="button"
        onClick={() => handleSort(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${
          sortKey === columnKey ? 'text-gray-900' : 'text-gray-600'
        }`}
      >
        <span>{t(labelKey, defaultLabel)}</span>
        <SortIcon columnKey={columnKey} />
      </button>
    </th>
  );

  /** One clickable stats cell. Active state mirrors the status filter. */
  const statCell = (step: TaskStep) => {
    const active = statusFilter === step.status;
    return (
      <td key={step.status} className="px-4 py-3">
        <button
          type="button"
          onClick={() => toggleStatus(step.status)}
          aria-pressed={active}
          title={taskLabel(step)}
          className={`text-lg font-bold transition-colors ${
            active ? 'text-blue-600 underline' : 'text-gray-900 hover:text-blue-600'
          }`}
        >
          {loading ? '—' : (counts[step.status] || 0).toLocaleString()}
        </button>
      </td>
    );
  };

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span>{t('admin:sidebar.activityManagement')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">
            {t('matches.title', '캠페인 지원자 관리')}
          </span>
        </nav>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 pb-16 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('matches.title', '캠페인 지원자 관리')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t(
              'matches.pageSub',
              '전 캠페인의 지원 건을 처리 단계별로 통합 조회 · 누가 어떤 캠페인에 지원했는지 한 화면에서 확인합니다.',
            )}
          </p>
        </div>

        {/* Stats — the work queue. Each number filters the table to that task. */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                    {t('matches.stats.totalMatches', '총 지원')}
                  </th>
                  {TASK_PIPELINE.map((s) => (
                    <th
                      key={s.status}
                      className="px-4 py-3 text-left font-medium whitespace-nowrap"
                    >
                      {taskLabel(s)}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                    {t('matches.task.completed', '완료')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="px-4 py-3 text-lg font-bold text-gray-900">
                    {loading ? '—' : rows.length.toLocaleString()}
                  </td>
                  {TASK_PIPELINE.map(statCell)}
                  {statCell(TERMINAL_STEPS[0])}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          >
            <option value="">{t('matches.filter.allTasks', '전체 처리 단계')}</option>
            {ALL_STEPS.map((s) => (
              <option key={s.status} value={s.status}>
                {taskLabel(s)}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={influencerQuery}
            onChange={(e) => {
              setInfluencerQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('matches.searchInfluencer', '인플루언서 닉네임 · 핸들 검색')}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          />

          <input
            type="text"
            value={campaignQuery}
            onChange={(e) => {
              setCampaignQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('matches.searchCampaign', '캠페인명 · 지역 검색')}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
          />

          {(statusFilter || influencerQuery.trim() || campaignQuery.trim()) && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter('');
                setInfluencerQuery('');
                setCampaignQuery('');
                setPage(1);
              }}
              className="px-3 py-2 text-sm text-red-600 hover:text-red-800 whitespace-nowrap"
            >
              {t('matches.clearFilters', '필터 초기화')}
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {error && <div className="px-4 py-6 text-sm text-red-600">{error}</div>}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {headerCell('influencer', 'matches.col.influencer', '인플루언서')}
                  {headerCell('campaign', 'matches.col.campaign', '캠페인')}
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                    {t('matches.col.channelCategory', '채널 · 카테고리')}
                  </th>
                  {headerCell('followers', 'matches.col.followers', '팔로워')}
                  {headerCell('status', 'matches.col.task', '처리 단계')}
                  {headerCell('appliedAt', 'matches.col.appliedAt', '지원일시')}
                  {headerCell('updatedAt', 'matches.col.updatedAt', '상태 갱신일시')}
                  <th className="px-4 py-3 text-left font-medium">
                    {t('matches.col.detail', '상세')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center">
                      <Spinner size="medium" />
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                      {t('matches.empty', '매칭 데이터가 없습니다.')}
                    </td>
                  </tr>
                ) : (
                  paged.map((r) => {
                    const step = STEP_BY_STATUS[r.status];
                    return (
                      <tr key={r.matchId} className="border-t border-gray-100 hover:bg-gray-50">
                        {/* Influencer */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {r.profileImage ? (
                              <img
                                src={r.profileImage}
                                alt=""
                                className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-[11px] text-gray-500 flex-shrink-0">
                                {(r.influencerName || '?')[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-gray-900 font-medium truncate">
                                {r.influencerName}
                              </div>
                              {r.handle && (
                                <div className="text-xs text-gray-400 truncate">{r.handle}</div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Campaign */}
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{r.campaignName}</div>
                          {r.campaignRegion && (
                            <div className="text-xs text-gray-400">{r.campaignRegion}</div>
                          )}
                        </td>

                        {/* Channels + category */}
                        <td className="px-4 py-3 text-gray-600">
                          {r.channels.length > 0 ? (
                            <span className="flex items-center gap-1.5">
                              {r.channels.map((c) => (
                                <ChannelIcon key={c} channel={c} className="w-4 h-4" />
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                          {r.categories.length > 0 && (
                            <div
                              className="mt-1 text-xs text-gray-500"
                              // Full list in the tooltip so nothing is lost when truncated.
                              title={r.categories
                                .map((c) => t(`common:categories.${c}`, c))
                                .join(', ')}
                            >
                              {r.categories
                                .slice(0, 2)
                                .map((c) => t(`common:categories.${c}`, c))
                                .join(', ')}
                              {r.categories.length > 2 && ` +${r.categories.length - 2}`}
                            </div>
                          )}
                        </td>

                        {/* Followers */}
                        <td className="px-4 py-3 text-gray-700 tabular-nums">
                          {fmtFollowers(r.followers)}
                        </td>

                        {/* Task */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              step?.badgeClass || 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {step ? taskLabel(step) : r.status}
                          </span>
                          {step?.ownerKey && (
                            <div className="mt-1 text-xs text-gray-400">
                              {t('matches.ownerPrefix', '담당')}:{' '}
                              {t(step.ownerKey, step.defaultOwner || '')}
                            </div>
                          )}
                        </td>

                        {/* Applied at */}
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap tabular-nums">
                          {fmtDateTime(r.appliedAt)}
                        </td>

                        {/* Status updated */}
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap tabular-nums">
                          {fmtDateTime(r.updatedAt)}
                        </td>

                        {/* Detail */}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/campaigns/${r.campaignId}/applicants`)}
                            className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                          >
                            {t('matches.viewApplicants', '지원자 관리')}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm text-gray-600">
              {t('matches.totalCount', '총 {{count}}건', { count: total })}
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                {t('admin:users.pagination.prev')}
              </button>

              {pageNumbers[0] > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    1
                  </button>
                  {pageNumbers[0] > 2 && <span className="px-1 text-gray-400">…</span>}
                </>
              )}

              {pageNumbers.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 text-sm border rounded-md ${
                    p === safePage
                      ? 'border-blue-600 bg-blue-50 text-blue-700 font-medium'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ))}

              {pageNumbers[pageNumbers.length - 1] < pageCount && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < pageCount - 1 && (
                    <span className="px-1 text-gray-400">…</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setPage(pageCount)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {pageCount}
                  </button>
                </>
              )}

              <button
                type="button"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
              >
                {t('admin:users.pagination.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignMatchesPage;
